/**
 * @file     mlsImageLoader.ts
 * @purpose  Custom next/image loader for MLS photos. Points the browser
 *           directly at the /api/mls/media proxy with a per-device width, so
 *           the proxy is the SINGLE image optimizer. This avoids the previous
 *           double transform (Next's /_next/image optimizer fetching the proxy
 *           which itself ran sharp) that made mobile photo loads take ~9s each.
 *
 * Usage: <Image src={`/api/mls/media/${mediaKey}`} loader={mlsImageLoader} ... />
 * The proxy resizes to `w`, re-encodes WebP, and is CDN-cached per URL.
 * Compliance: the proxy enforces the host allowlist + watermark preservation;
 * this loader only shapes the URL.
 */

import type { ImageLoaderProps } from 'next/image';

const PROXY_PREFIX = '/api/mls/media/';
// Off-market (VOW) photos go through their own signed, session-gated proxy.
// It takes the same ?w/?q contract, so it can be width-sized identically.
const VOW_PROXY_PREFIX = '/api/vow/media';
// Owner listing photos (not MLS): already re-encoded WebP derivatives in the
// public listing bucket, or a short-lived signed URL for a draft preview.
// They render as-is; the gallery shares this loader with MLS media.
const OWNER_LISTING_MEDIA = /^https:\/\/[a-z0-9-]+\.supabase\.co\/storage\/v1\/object\/(public\/listing-media-public|sign\/listing-media)\//i;

export function mlsImageLoader({ src, width, quality }: ImageLoaderProps): string {
  // `src` should be the bare proxy path, e.g. /api/mls/media/{mediaKey}. If a
  // stale cache or upstream surface ever hands us a raw CDN URL (e.g.
  // https://ddfcdn.realtor.ca/...jpg from a pre-fix cached ListingCard),
  // appending ?w=&q= would emit a request the browser CSP-blocks — and the
  // broken URL is harder to diagnose than a clean placeholder. Degrade
  // gracefully instead. Local non-proxy paths (e.g. /landing-demo/x.jpg) pass
  // through unchanged.
  if (OWNER_LISTING_MEDIA.test(src)) return src;
  if (/^https?:\/\//i.test(src)) return MLS_PHOTO_PLACEHOLDER;
  if (!src.startsWith(PROXY_PREFIX) && !src.startsWith(VOW_PROXY_PREFIX)) return src;
  const q = quality ?? 78;
  const sep = src.includes('?') ? '&' : '?';
  return `${src}${sep}w=${width}&q=${q}`;
}

/**
 * mlsImageLoader with a width ceiling. next/image fills `src` with the widest
 * srcset candidate (1920), and crawlers fetch `src`, not srcset: they are
 * nearly all photo-proxy traffic, so an uncapped 320px card ships a ~200 KB
 * derivative where ~52 KB serves it. Browsers still pick from srcset, so the
 * cap only changes what they get past `maxWidth`. Keep `maxWidth` on the
 * MEDIA_WIDTHS ladder (mediaDerivativeCache.ts) so the capped URL is a rung.
 */
export function mlsImageLoaderMax(maxWidth: number): (props: ImageLoaderProps) => string {
  return (props) => mlsImageLoader({ ...props, width: Math.min(props.width, maxWidth) });
}

/** Cards and thumbnails: 750 is the full-width phone card rung. */
export const mlsCardImageLoader = mlsImageLoaderMax(750);

/** Detail hero and carousel: 1280 covers the 568px desktop hero at 2x. */
export const mlsHeroImageLoader = mlsImageLoaderMax(1280);

/**
 * Pick the display src for a detail-gallery photo. VOW (off-market) media is
 * served pre-signed through its own gated proxy and its mediaKey is NOT in
 * mls_media, so routing it through the MLS proxy 404s every photo. When the
 * record carries a signed VOW url, use it directly; otherwise build the MLS
 * proxy path from the key. mlsImageLoader already accepts both prefixes, so the
 * gallery sizes either one the same way.
 */
export function detailMediaSrc(mediaKey: string, mediaUrl: string | null | undefined): string {
  if (mediaUrl && mediaUrl.startsWith(VOW_PROXY_PREFIX)) return mediaUrl;
  return `${PROXY_PREFIX}${encodeURIComponent(mediaKey)}`;
}

/**
 * Build a sized proxy URL for plain <img>/srcset use (cards, tiles, gallery).
 * Only rewrites our own proxy paths — curated/direct CDN URLs pass through
 * untouched so we never corrupt a non-proxy src. The proxy is the single
 * optimizer (WebP, CDN-cached), so this avoids the /_next/image double hop.
 */
export function mlsProxyUrl(src: string, width: number, quality = 78): string {
  if (!src.startsWith(PROXY_PREFIX)) return src;
  const sep = src.includes('?') ? '&' : '?';
  return `${src}${sep}w=${width}&q=${quality}`;
}

/** Comma-joined srcset of proxy URLs at the given widths, for responsive <img>. */
export function mlsProxySrcSet(src: string, widths: number[], quality = 78): string | undefined {
  if (!src.startsWith(PROXY_PREFIX)) return undefined;
  return widths.map((w) => `${mlsProxyUrl(src, w, quality)} ${w}w`).join(', ');
}

/**
 * Neutral inline placeholder shown when a photo fails to load. Inline data-URI
 * (no network request, can't itself fail), so both the grid card and the detail
 * gallery degrade to the SAME graceful state instead of a broken-image icon
 * (detail) or a blank collapsed tile (card). A muted "no photo" camera glyph on
 * a light surface; deliberately theme-agnostic since it renders inside <img>.
 */
export const MLS_PHOTO_PLACEHOLDER =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="120" viewBox="0 0 160 120">' +
      '<rect width="160" height="120" fill="#eceef1"/>' +
      '<g fill="none" stroke="#b8bec7" stroke-width="3" stroke-linejoin="round">' +
      '<rect x="54" y="48" width="52" height="34" rx="4"/>' +
      '<circle cx="80" cy="65" r="9"/>' +
      '<path d="M66 48l5-7h18l5 7"/></g></svg>',
  );

/**
 * Idempotent <img> onError handler: swap to the placeholder once and stop, so a
 * failing src can't loop (the placeholder is a data-URI and never re-fires).
 */
export function applyPhotoFallback(img: HTMLImageElement): void {
  if (img.dataset.fallbackApplied === '1') return;
  img.dataset.fallbackApplied = '1';
  img.removeAttribute('srcset');
  img.src = MLS_PHOTO_PLACEHOLDER;
}
